import React, { useEffect, useState } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Plus, Minus } from "lucide-react";
import { addDoc, collection, query, where, getDocs, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import featureGatingService from "../../services/featureGatingService";
import { toast } from "react-toastify";
import { ncltGet, NCLT_API_BASE_URL } from "../../services/ncltApi";
import { buildNcltParties } from "../../utils/ncltParties";

function AccordionSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="mt-6 bg-white border border-slate-200 shadow-sm rounded-[20px] overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 bg-slate-50 text-slate-700 font-semibold text-lg"
      >
        <span className="text-yellow-400">{title}</span>
        {open ? <Minus size={22} /> : <Plus size={22} />}
      </button>

      {open && (
        <div className="px-6 py-6 text-white">
          {children}
        </div>
      )}
    </div>
  );
}

export default function NcltCaseFullDetails() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const API_BASE_URL = NCLT_API_BASE_URL;

  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    // If case data was passed directly (from CIN/Filing search)/////
    if (state?.caseData) {
      setCaseData(state.caseData);
      setLoading(false);
      return;
    }

    // If filing number was passed (from case list click)
    if (state?.filingNumber) {
      ncltGet(`/filing/${state.filingNumber}`)
        .then((res) => {
          setCaseData(res.body);
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setLoading(false);
        });
      return;
    }

    console.error("Missing caseData or filingNumber");
    setLoading(false);
  }, [state]);

  const exportCase = async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.error("User not logged in");
      return;
    }
    if (!caseData?.header) return;

    setExporting(true);

    try {
      const { header, currentDetails, partyDetails, proceedingDetails, applicationDetails, parentCaseDetails } = caseData;

      // Determine target collection for limit check
      const caseStage = currentDetails?.case_stage || "";
      const targetCollection = caseStage.toLowerCase().includes("disposed") ? "disposed" : "pending";

      // Check case limit before proceeding
      const limitResult = await featureGatingService.checkCaseLimitBeforeExport(
        user.uid,
        1,
        targetCollection
      );

      if (!limitResult.canProceed) {
        toast.error(`Case limit reached! Your ${limitResult.planName} plan allows ${limitResult.maxLimit} ${targetCollection} cases. Please upgrade to add more.`);
        setExporting(false);
        return;
      }

      // Check for duplicate
      const filingNo = header.filingNo || "";
      if (filingNo) {
        const pendingQ = query(collection(db, "pending"), where("filingNo", "==", filingNo), where("owner", "==", user.uid));
        const disposedQ = query(collection(db, "disposed"), where("filingNo", "==", filingNo), where("owner", "==", user.uid));
        const [pendingSnap, disposedSnap] = await Promise.all([getDocs(pendingQ), getDocs(disposedQ)]);
        if (!pendingSnap.empty || !disposedSnap.empty) {
          toast.warning("This case has already been exported");
          setExporting(false);
          return;
        }
      }

      // Parse parties
      const partyName = header.partyName || "";
      const [petitioner = "", respondent = ""] = partyName.split(/\s+VS\s+/i);

      const caseYearMatch = (header.regNo || "").match(/\/(\d{4})/);
      const caseYear = caseYearMatch ? caseYearMatch[1] : "";

      const normalizedStatus = targetCollection;

      // Subscription metadata
      let subscriptionMetadata = null;
      if (normalizedStatus === "pending") {
        const pendingQuery = query(collection(db, "pending"), where("owner", "==", user.uid));
        const pendingSnapshot = await getDocs(pendingQuery);
        subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(
          user.uid,
          pendingSnapshot.size
        );
      }

      // Petitioners / respondents and their advocates from partyDetails by party_type.
      // P1 => main petitioner, P2/P3... => other petitioners; R1 => main respondent, R2/R3... => other respondents.
      const parties = buildNcltParties(partyDetails, petitioner, respondent);

      // Proceeding history
      const proceedings = proceedingDetails || [];
      const lastProceeding = proceedings[proceedings.length - 1];
      const normalizeDate = (d) => d ? d.replace(/-/g, '/') : "";
      const previousHearingDate = normalizeDate(lastProceeding?.listing_date);
      const nextHearingDate = normalizeDate(lastProceeding?.next_list_or_dispose_date);

      const caseHistory = proceedings.map((h) => ({
        causeListType: "NCLT",
        judge: "—",
        businessOnDate: h.order_upload_datetime ? h.order_upload_datetime.replace(/-/g, '/') : "—",
        hearingDate: h.listing_date ? h.listing_date.replace(/-/g, '/') : "—",
        purpose: h.listing_purpose || "Listing / Order",
      }));

      const regNo = header.regNo || filingNo;
      const caseDoc = {
        caseNumber: regNo,
        caseNo: regNo,
        filingNo,
        diaryNo: filingNo,

        caseType: currentDetails?.case_type || "NCLT",
        court: "NCLT",
        courtName: "NCLT",

        petitionerName: parties.petitionerName,
        respondentName: parties.respondentName,
        allPetitioners: parties.allPetitioners,
        allRespondents: parties.allRespondents,

        caseFiledDate: currentDetails?.filing_date || "",
        caseYear,
        previousHearingDate,
        nextHearingDate,

        status: normalizedStatus,
        caseHistory,

        petitionerAdvocate: parties.petitionerAdvocate,
        respondentAdvocate: parties.respondentAdvocate,
        allPetitionerAdvocates: parties.allPetitionerAdvocates,
        allRespondentAdvocates: parties.allRespondentAdvocates,

        owner: user.uid,
        team: [user.uid],
        createdAt: new Date(),
        timestamp: serverTimestamp(),

        rawNcltData: { fullDetails: caseData },

        payload: {
          source: "NCLT",
          statusText: caseStage,
          exportedAt: new Date().toISOString(),
        },

        ncltProceedingDetails: proceedings,
        ncltApplicationDetails: applicationDetails || [],
        ncltMainCaseDetails: parentCaseDetails || [],

        files: [],
        casenotes: "",
        caseFilesLink: "",
        ...(subscriptionMetadata && { subscriptionMetadata }),
      };

      const docRef = await addDoc(collection(db, normalizedStatus), caseDoc);
      const caseId = docRef.id;
      await updateDoc(docRef, { caseFilesLink: `/portal/case-files/${caseId}?from=nclt` });

      // Create hearing reminder events (only if next hearing date is in the future)
      try {
        const parseHearingDate = (str) => {
          if (!str) return null;
          const iso = str.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
          if (iso) return new Date(parseInt(iso[1]), parseInt(iso[2]) - 1, parseInt(iso[3]));
          const dmy = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
          if (dmy) return new Date(parseInt(dmy[3]), parseInt(dmy[2]) - 1, parseInt(dmy[1]));
          return null;
        };
        const formatYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

        const eventDate = parseHearingDate(nextHearingDate);
        if (eventDate && !isNaN(eventDate.getTime())) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const hearingDay = new Date(eventDate);
          hearingDay.setHours(0, 0, 0, 0);

          if (hearingDay >= today) {
            const reminderDate = new Date(eventDate);
            reminderDate.setHours(8, 0, 0, 0);
            const eventTimeIST = new Date(eventDate);
            eventTimeIST.setHours(18, 0, 0, 0);
            const now = new Date();

            if (reminderDate > now) {
              const reminderEventData = {
                "Case No": regNo,
                date: formatYMD(reminderDate),
                id: Math.floor(Math.random() * 90000) + 10000,
                time: "08:00 AM",
                time1: reminderDate.getTime(),
                title: `${petitioner.trim() || "—"} VS ${respondent.trim() || "—"}`,
                description: `Hearing Reminder : Case No. - ${regNo}`,
                isScheduled: true,
                status: normalizedStatus,
                eventType: "reminder",
              };
              const reminderEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), reminderEventData);
              await addDoc(collection(db, "eventReminders"), {
                eventId: reminderEventRef.id,
                reminderTime: reminderDate,
                eventTitle: `Hearing Reminder : Case No. - ${regNo}`,
                caseNo: regNo,
                caseId: caseId,
                collection: normalizedStatus,
                status: "pending",
                createdAt: new Date(),
                scheduledBy: user.uid,
                recipientId: user.uid,
              });
            }

            if (eventTimeIST > now) {
              const mainEventData = {
                "Case No": regNo,
                date: formatYMD(eventDate),
                id: Math.floor(Math.random() * 90000) + 10000,
                time: "06:00 PM",
                time1: eventTimeIST.getTime(),
                title: `Update Next Hearing: ${petitioner.trim() || "—"} VS ${respondent.trim() || "—"}`,
                description: `Case No. - ${regNo}`,
                isScheduled: true,
                status: normalizedStatus,
                eventType: "updateReminder",
                isHidden: true,
              };
              const mainEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), mainEventData);
              await addDoc(collection(db, "eventReminders"), {
                eventId: mainEventRef.id,
                reminderTime: eventTimeIST,
                eventTitle: `Update Next Hearing : ${regNo}`,
                caseNo: regNo,
                caseId: caseId,
                collection: normalizedStatus,
                status: "pending",
                createdAt: new Date(),
                scheduledBy: user.uid,
                recipientId: user.uid,
              });
            }
          } else {
            console.log("NCLT: skipping event creation — next hearing date is in the past:", nextHearingDate);
          }
        }
      } catch (notificationError) {
        console.error("NCLT notification setup error:", notificationError);
      }

      // Save order PDFs from proceedingDetails into an "Orders" folder
      if (Array.isArray(proceedingDetails)) {
        const ordersWithPath = proceedingDetails.filter(h => h.encPath);
        if (ordersWithPath.length > 0) {
          const orderFolderRef = await addDoc(collection(db, "folders"), {
            name: "Orders",
            caseId,
            caseNo: caseDoc.caseNumber,
            lawyer_uid: user.uid,
            createdAt: new Date().toISOString(),
          });

          for (const h of ordersWithPath) {
            const pdfUrl = `${API_BASE_URL}/order?encPath=${encodeURIComponent(h.encPath)}`;
            const orderDate = h.listing_date || "Unknown Date";
            const orderName = h.order_name || "Order";

            await addDoc(collection(db, "files"), {
              caseNo: caseDoc.caseNumber,
              fileName: `${orderName} - ${orderDate}`,
              file_url: pdfUrl,
              lawyer_uid: user.uid,
              uploadedAt: h.order_upload_datetime || new Date().toISOString(),
              source: "NCLT",
              folderId: orderFolderRef.id,
            });
          }
        }
      }

      toast.success("Case exported successfully!");
    } catch (err) {
      console.error("NCLT export failed:", err);
      toast.error("Failed to export case");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <>
                                <div className="min-h-screen text-white flex items-center justify-center">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
            <span className="text-yellow-500 text-lg font-semibold">Fetching cases, Please wait until magic happens.</span>
          </div>
        </div>
      </>
    );
  }

  if (!caseData || !caseData.header) {
    return (
      <>
                                <div className="min-h-screen text-white flex items-center justify-center">
          Failed to load case
        </div>
      </>
    );
  }

  const { header, currentDetails, partyDetails, applicationDetails, parentCaseDetails, disposedDetails, proceedingDetails, nclatDetails, defectiveDates } = caseData;

  return (
    <>
                        <div className="relative min-h-screen flex flex-col px-4 md:px-16 py-8">
          <div className="mb-4"><FindCaseStepper current={4} /></div>
        <div className="max-w-6xl mx-auto w-full">
        {/* HEADER */}
        <div className="relative z-10 flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-white hover:text-yellow-400"
            >
              <ArrowLeft size={28} />
            </button>

            <h1 className="text-yellow-500 text-2xl font-bold">
              Case Details
            </h1>
          </div>

          <button
            onClick={exportCase}
            disabled={exporting}
            className={`px-6 py-2 rounded-lg font-bold ${
              exporting
                ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                : "bg-yellow-500 text-black hover:bg-yellow-400"
            }`}
          >
            {exporting ? "Importing..." : "Import Case"}
          </button>
        </div>

        {/* CASE HEADER INFO */}
        <div className="bg-white border border-slate-200 shadow-sm text-slate-800 rounded-[20px] p-6 relative z-10">
          <table className="w-full border-collapse">
            <tbody>
              <tr>
                <td className="p-3 font-semibold border border-gray-700 bg-slate-50 w-1/4">Filing Number</td>
                <td className="p-3 border border-gray-700">{header.filingNo || "—"}</td>
                <td className="p-3 font-semibold border border-gray-700 bg-slate-50 w-1/4">Registration No</td>
                <td className="p-3 border border-gray-700">{header.regNo || "—"}</td>
              </tr>

              <tr>
                <td className="p-3 font-semibold border border-gray-700 bg-slate-50">Party Name</td>
                <td className="p-3 border border-gray-700" colSpan="3">
                  {(() => {
                    const parts = (header.partyName || "").split(/\s+VS\s+/i);
                    if (parts.length < 2) return header.partyName || "—";
                    return (
                      <>
                        <div>
                          {parts[0].trim()}{" "}
                          <span className="text-yellow-400 font-semibold">VS</span>
                        </div>
                        <div>{parts.slice(1).join(" VS ").trim()}</div>
                      </>
                    );
                  })()}
                </td>
              </tr>

              <tr>
                <td className="p-3 font-semibold border border-gray-700 bg-slate-50">All Hearings</td>
                <td className="p-3 border border-gray-700">{header.allHearings || "—"}</td>
                <td className="p-3 font-semibold border border-gray-700 bg-slate-50">Effective Hearings</td>
                <td className="p-3 border border-gray-700">{header.effHearings || "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* CURRENT DETAILS */}
        {currentDetails && (
          <AccordionSection title="Current Case Details" defaultOpen={true}>
            <table className="w-full border-collapse">
              <tbody>
                <tr>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50 w-1/4">Filing Date</td>
                  <td className="p-3 border border-gray-700">{currentDetails.filing_date || "—"}</td>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50 w-1/4">Case Type</td>
                  <td className="p-3 border border-gray-700">{currentDetails.case_type || "—"}</td>
                </tr>
                <tr>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50">Case Filed By</td>
                  <td className="p-3 border border-gray-700">{currentDetails.case_filed_by || "—"}</td>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50">Defect Issued Date</td>
                  <td className="p-3 border border-gray-700">{currentDetails.defect_issued_date || "—"}</td>
                </tr>
                <tr>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50">Refiled Date</td>
                  <td className="p-3 border border-gray-700">{currentDetails.refiled_date || "—"}</td>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50">Defect Free</td>
                  <td className="p-3 border border-gray-700">{currentDetails.defect_free || "—"}</td>
                </tr>
                <tr>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50">Registration Date</td>
                  <td className="p-3 border border-gray-700">{currentDetails.registration_date || "—"}</td>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50">Court No</td>
                  <td className="p-3 border border-gray-700">{currentDetails.court_no || "—"}</td>
                </tr>
                <tr>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50">Bench Nature</td>
                  <td className="p-3 border border-gray-700">{currentDetails.bench_nature || "—"}</td>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50">First Listing Date</td>
                  <td className="p-3 border border-gray-700">{currentDetails.first_listing_date || "—"}</td>
                </tr>
                <tr>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50">e-Filed / Backlog</td>
                  <td className="p-3 border border-gray-700">{currentDetails.efiled_or_backlog || "—"}</td>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50">Case Stage</td>
                  <td className="p-3 border border-gray-700">
                    <span className={
                      currentDetails.case_stage?.toLowerCase().includes("disposed")
                        ? "text-red-400 font-semibold"
                        : "text-green-400 font-semibold"
                    }>
                      {currentDetails.case_stage || "—"}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50">Case Status</td>
                  <td className="p-3 border border-gray-700">
                    <span className={
                      currentDetails.case_stage?.toLowerCase().includes("disposed")
                        ? "text-red-400 font-semibold"
                        : "text-green-400 font-semibold"
                    }>
                      {currentDetails.case_stage?.toLowerCase().includes("disposed") ? "Disposed" : "Pending"}
                    </span>
                  </td>
                  <td className="p-3 font-semibold border border-gray-700 bg-slate-50"></td>
                  <td className="p-3 border border-gray-700"></td>
                </tr>
              </tbody>
            </table>
          </AccordionSection>
        )}

        {/* PARTY DETAILS */}
        <AccordionSection title="Party Details">
          {(!partyDetails || partyDetails.length === 0) ? (
            <div className="text-slate-500">No Data Found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-slate-50 text-white">
                  <tr>
                    <th className="border border-gray-700 px-3 py-2">S. No</th>
                    <th className="border border-gray-700 px-3 py-2">Party Type</th>
                    <th className="border border-gray-700 px-3 py-2">Party Name</th>
                    <th className="border border-gray-700 px-3 py-2">Advocate Name</th>
                    <th className="border border-gray-700 px-3 py-2">Email</th>
                    <th className="border border-gray-700 px-3 py-2">Mobile</th>
                  </tr>
                </thead>
                <tbody>
                  {partyDetails.map((p, i) => (
                    <tr key={i} className="hover:bg-blue-900/20">
                      <td className="border border-gray-700 px-3 py-2 text-center">{p.sno}</td>
                      <td className="border border-gray-700 px-3 py-2">{p.party_type || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{p.party_name || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{p.advocate_name || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{p.party_email || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{p.party_mobile || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AccordionSection>

        {/* PROCEEDING DETAILS */}
        <AccordionSection title="Proceeding Details (Orders)" defaultOpen={true}>
          {(!proceedingDetails || proceedingDetails.length === 0) ? (
            <div className="text-slate-500">No Data Found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-slate-50 text-white">
                  <tr>
                    <th className="border border-gray-700 px-3 py-2">S. No</th>
                    <th className="border border-gray-700 px-3 py-2">Bench</th>
                    <th className="border border-gray-700 px-3 py-2">Court No</th>
                    <th className="border border-gray-700 px-3 py-2">Listing Date</th>
                    <th className="border border-gray-700 px-3 py-2">Purpose</th>
                    <th className="border border-gray-700 px-3 py-2">Action Taken</th>
                    <th className="border border-gray-700 px-3 py-2">Next Date</th>
                    <th className="border border-gray-700 px-3 py-2">Next Purpose</th>
                    <th className="border border-gray-700 px-3 py-2">Order Upload Date/Time</th>
                    <th className="border border-gray-700 px-3 py-2">Status</th>
                    <th className="border border-gray-700 px-3 py-2">Order</th>
                  </tr>
                </thead>
                <tbody>
                  {proceedingDetails.map((p, i) => (
                    <tr key={i} className="hover:bg-blue-900/20">
                      <td className="border border-gray-700 px-3 py-2 text-center">{p.sno}</td>
                      <td className="border border-gray-700 px-3 py-2">{p.bench_location || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{p.court_no || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{p.listing_date || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{p.listing_purpose || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{p.action_taken || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{p.next_list_or_dispose_date || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{p.next_listing_purpose || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{p.order_upload_datetime || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">
                        <span className={
                          p.status?.toLowerCase() === "dispose"
                            ? "text-red-400 font-semibold"
                            : "text-green-400 font-semibold"
                        }>
                          {p.status || "—"}
                        </span>
                      </td>
                      <td className="border border-gray-700 px-3 py-2">
                        {p.encPath ? (
                          <a
                            href={`${API_BASE_URL}/order?encPath=${encodeURIComponent(p.encPath)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-colors"
                            title={p.order_name || "Order"}
                          >
                            View
                          </a>
                        ) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AccordionSection>

        {/* APPLICATION DETAILS */}
        <AccordionSection title="Application Details (IA / MA)">
          {(!applicationDetails || applicationDetails.length === 0) ? (
            <div className="text-slate-500">No Data Found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1080px] border-collapse">
                <thead className="bg-slate-50 text-white">
                  <tr>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">S. No</th>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">Filing No</th>
                    <th className="border border-gray-700 px-3 py-2">Case Type</th>
                    <th className="border border-gray-700 px-3 py-2">Case Title</th>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">Bench / Court</th>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">Application No</th>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">Filing / Reg Date</th>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">Next / Dispose Date</th>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {applicationDetails.map((a, i) => (
                    <tr key={i} className="hover:bg-blue-900/20 align-top">
                      <td className="border border-gray-700 px-3 py-2 text-center whitespace-nowrap">{a.sno || i + 1}</td>
                      <td className="border border-gray-700 px-3 py-2 whitespace-nowrap">{a.filing_no || a.filingNo || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2 min-w-[150px]">{a.case_type || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2 min-w-[220px]">
                        {(a.case_title1 || a.case_title2) ? (
                          <>
                            <div>{a.case_title1 || "—"}</div>
                            {a.case_title2 ? (
                              <>
                                <span className="text-yellow-400 font-semibold">VS</span>
                                <div>{a.case_title2}</div>
                              </>
                            ) : null}
                          </>
                        ) : "—"}
                      </td>
                      <td className="border border-gray-700 px-3 py-2 whitespace-nowrap">
                        {a.bench_location || "—"}{a.court_no ? ` / ${a.court_no}` : ""}
                      </td>
                      <td className="border border-gray-700 px-3 py-2 whitespace-nowrap">{a.application_no || a.caseNumber || a.case_number || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2 whitespace-nowrap">
                        {a.filing_date || "—"}{a.registration_date ? ` / ${a.registration_date}` : ""}
                      </td>
                      <td className="border border-gray-700 px-3 py-2 whitespace-nowrap">
                        {(a.next_list_date && a.next_list_date !== "NA") ? a.next_list_date
                          : (a.disposal_date && a.disposal_date !== "NA") ? a.disposal_date
                          : (a.next_list_or_dispose_date || "—")}
                      </td>
                      <td className="border border-gray-700 px-3 py-2 whitespace-nowrap">
                        <span className={
                          (a.case_status || a.status || "").toLowerCase().includes("dispos")
                            ? "text-red-400 font-semibold"
                            : "text-green-400 font-semibold"
                        }>
                          {a.case_status || a.status || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AccordionSection>

        {/* PARENT / MAIN CASE DETAILS */}
        {parentCaseDetails && parentCaseDetails.length > 0 && (
          <AccordionSection title="Main / Parent Case" defaultOpen={true}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1080px] border-collapse">
                <thead className="bg-slate-50 text-white">
                  <tr>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">S. No</th>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">Filing No</th>
                    <th className="border border-gray-700 px-3 py-2">Case Type</th>
                    <th className="border border-gray-700 px-3 py-2">Case Title</th>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">Bench / Court</th>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">Case No</th>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">Filing / Reg Date</th>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">Next / Dispose Date</th>
                    <th className="border border-gray-700 px-3 py-2 whitespace-nowrap">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parentCaseDetails.map((m, i) => (
                    <tr key={i} className="hover:bg-blue-900/20 align-top">
                      <td className="border border-gray-700 px-3 py-2 text-center whitespace-nowrap">{m.sno || i + 1}</td>
                      <td className="border border-gray-700 px-3 py-2 whitespace-nowrap">{m.filing_no || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2 min-w-[150px]">{m.case_type || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2 min-w-[220px]">
                        {(m.case_title1 || m.case_title2) ? (
                          <>
                            <div>{m.case_title1 || "—"}</div>
                            {m.case_title2 ? (
                              <>
                                <span className="text-yellow-400 font-semibold">VS</span>
                                <div>{m.case_title2}</div>
                              </>
                            ) : null}
                          </>
                        ) : "—"}
                      </td>
                      <td className="border border-gray-700 px-3 py-2 whitespace-nowrap">
                        {m.bench_location || "—"}{m.court_no ? ` / ${m.court_no}` : ""}
                      </td>
                      <td className="border border-gray-700 px-3 py-2 whitespace-nowrap">{m.case_no || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2 whitespace-nowrap">
                        {m.filing_date || "—"}{m.registration_date ? ` / ${m.registration_date}` : ""}
                      </td>
                      <td className="border border-gray-700 px-3 py-2 whitespace-nowrap">
                        {(m.next_list_date && m.next_list_date !== "NA") ? m.next_list_date
                          : (m.disposal_date && m.disposal_date !== "NA") ? m.disposal_date
                          : "—"}
                      </td>
                      <td className="border border-gray-700 px-3 py-2 whitespace-nowrap">
                        <span className={
                          (m.case_status || "").toLowerCase().includes("dispos")
                            ? "text-red-400 font-semibold"
                            : "text-green-400 font-semibold"
                        }>
                          {m.case_status || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AccordionSection>
        )}

        {/* DISPOSED DETAILS */}
        <AccordionSection title="Disposed Details">
          {(!disposedDetails || disposedDetails.length === 0) ? (
            <div className="text-slate-500">No Data Found</div>
          ) : (
            <table className="w-full border-collapse">
              <thead className="bg-slate-50 text-white">
                <tr>
                  <th className="border border-gray-700 px-3 py-2">S. No</th>
                  <th className="border border-gray-700 px-3 py-2">Disposal Date</th>
                  <th className="border border-gray-700 px-3 py-2">Disposed Nature</th>
                </tr>
              </thead>
              <tbody>
                {disposedDetails.map((d, i) => (
                  <tr key={i}>
                    <td className="border border-gray-700 px-3 py-2 text-center">{d.sno}</td>
                    <td className="border border-gray-700 px-3 py-2">{d.disposal_date || "—"}</td>
                    <td className="border border-gray-700 px-3 py-2 text-red-400">{d.disposed_nature || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </AccordionSection>

        {/* DEFECTIVE DATES */}
        {defectiveDates && defectiveDates.length > 0 && (
          <AccordionSection title="Defective Dates">
            <table className="w-full border-collapse">
              <thead className="bg-slate-50 text-white">
                <tr>
                  <th className="border border-gray-700 px-3 py-2">S. No</th>
                  <th className="border border-gray-700 px-3 py-2">Date</th>
                  <th className="border border-gray-700 px-3 py-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {defectiveDates.map((d, i) => (
                  <tr key={i}>
                    <td className="border border-gray-700 px-3 py-2 text-center">{i + 1}</td>
                    <td className="border border-gray-700 px-3 py-2">{d.defective_date || d.date || "—"}</td>
                    <td className="border border-gray-700 px-3 py-2">{d.details || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AccordionSection>
        )}

        {/* NCLAT DETAILS */}
        <AccordionSection title="NCLAT Details">
          {(!nclatDetails || nclatDetails.length === 0) ? (
            <div className="text-slate-500">No Data Found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-slate-50 text-white">
                  <tr>
                    <th className="border border-gray-700 px-3 py-2">S. No</th>
                    <th className="border border-gray-700 px-3 py-2">Case No</th>
                    <th className="border border-gray-700 px-3 py-2">Case Title</th>
                    <th className="border border-gray-700 px-3 py-2">Case Stage</th>
                    <th className="border border-gray-700 px-3 py-2">Status</th>
                    <th className="border border-gray-700 px-3 py-2">Action Taken</th>
                    <th className="border border-gray-700 px-3 py-2">Next Hearing Date</th>
                  </tr>
                </thead>
                <tbody>
                  {nclatDetails.map((n, i) => (
                    <tr key={i} className="hover:bg-blue-900/20">
                      <td className="border border-gray-700 px-3 py-2 text-center">{n.sno || i + 1}</td>
                      <td className="border border-gray-700 px-3 py-2">
                        {[n.case_type, n.case_no, n.party_search].filter(Boolean).join(" / ") || "—"}
                      </td>
                      <td className="border border-gray-700 px-3 py-2">{n.case_title || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{n.case_stage || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{n.case_status || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{n.action_taken || "—"}</td>
                      <td className="border border-gray-700 px-3 py-2">{n.next_hearing_date || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AccordionSection>

        </div>
      </div>
    </>
  );
}
